import React from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Award, ShieldCheck, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { CLINIC } from "@/lib/clinicInfo";

const EDUCATION = [
  { label: "MBBS", where: "Armed Forces Medical College, Pune" },
  { label: "MS Ophthalmology", where: "KGMU, Lucknow" },
  { label: "Fellowship Training", where: "LVPEI, Hyderabad" },
];

const HIGHLIGHTS = [
  { n: "3000+", l: "Cataract Surgeries" },
  { n: "500+", l: "Oculoplasty Procedures" },
  { n: "100+", l: "Corneal Surgeries" },
  { n: "Expert", l: "Refractive Surgery" },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Dr. Gurkiran Kaur — Fellowship Trained Ophthalmologist in Noida"
        description="Dr. Gurkiran Kaur — MBBS (AFMC Pune), MS Ophthalmology (KGMU), Fellowship (LVPEI Hyderabad). Founder of Nehchal Eye Centre, Sector 32 Noida."
        path="/about"
      />
      <Breadcrumbs items={[{ name: "About", path: "/about" }]} />
      <section className="container-wide pt-10 pb-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden border border-border bg-card">
              <img
                src={CLINIC.doctorPhoto}
                alt="Dr. Gurkiran Kaur, founder of Nehchal Eye Centre Noida"
                className="w-full h-[520px] object-cover object-top"
                loading="eager"
                width="480"
                height="520"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-widest text-accent mb-3">Meet the Founder</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05]">
              Dr. Gurkiran Kaur
            </h1>
            <p className="mt-3 text-primary">Founder & Director · Fellowship Trained Ophthalmologist</p>

            <p className="mt-8 text-foreground/80 leading-8">
              With 15+ years of clinical experience and over 3,000 successful eye surgeries, Dr. Gurkiran Kaur founded
              Nehchal Eye Centre with a simple philosophy — every patient deserves personalised, tailor-made eye care
              delivered with the honesty and rigour of a tertiary hospital.
            </p>
            <p className="mt-4 text-foreground/80 leading-8">
              Trained at some of India&apos;s finest institutions, she specialises in advanced cataract surgery including
              complex cases, oculoplasty and aesthetic procedures, corneal and refractive surgery, and
              neuro-ophthalmology.
            </p>

            <div className="mt-10">
              <div className="text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-4 flex items-center gap-2">
                <GraduationCap className="h-4 w-4" /> Educational Background
              </div>
              <ul className="space-y-3">
                {EDUCATION.map((e) => (
                  <li key={e.label} className="flex items-start gap-3 text-sm">
                    <div className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" />
                    <div>
                      <div className="font-medium text-foreground">{e.label}</div>
                      <div className="text-muted-foreground">{e.where}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {HIGHLIGHTS.map((h) => (
                <div key={h.l} className="rounded-lg border border-border bg-card p-4">
                  <div className="font-serif text-2xl text-primary">{h.n}</div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">{h.l}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/services"
                className="inline-flex items-center rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-primary/90"
                data-testid="about-services-btn"
              >
                Explore Services
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center rounded-full border border-border px-5 py-2.5 text-sm hover:bg-secondary"
                data-testid="about-book-btn"
              >
                Book a Consultation
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-secondary/40 border-y border-border py-16">
        <div className="container-wide grid md:grid-cols-4 gap-6">
          {[
            { i: <Award className="h-5 w-5" />, t: "Fellowship Trained", d: "LVPEI Hyderabad Certified Specialist" },
            { i: <Sparkles className="h-5 w-5" />, t: "Latest Technology", d: "State-of-the-art surgical equipment" },
            { i: <ShieldCheck className="h-5 w-5" />, t: "Safety First", d: "Highest hygiene & safety standards" },
            { i: <GraduationCap className="h-5 w-5" />, t: "Personal Care", d: "Individual attention for every patient" },
          ].map((x) => (
            <div key={x.t} className="rounded-xl border border-border bg-card p-6">
              <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3">
                {x.i}
              </div>
              <div className="font-serif text-lg">{x.t}</div>
              <div className="text-sm text-muted-foreground mt-1">{x.d}</div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
